import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EditableCard from '../EditableCard/EditableCard';
import { usePatchHouseMutation } from '../drApi/drApi';
import styles from './HouseSapSettingsCard.module.scss';

const HouseSapSettingsCard = ({ house }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSAPHouse, setIsSAPHouse] = useState(!!house.isSAPHouse);
  const [testDailyDataEnabled, setTestDailyDataEnabled] = useState(!!house.testDailyDataEnabled);
  const [patchHouse, { isLoading, isSuccess }] = usePatchHouseMutation();

  useEffect(() => {
    setIsSAPHouse(!!house.isSAPHouse);
    setTestDailyDataEnabled(!!house.testDailyDataEnabled);
  }, [house.isSAPHouse, house.testDailyDataEnabled]);

  useEffect(() => {
    if (isSuccess) {
      setIsEditing(false);
    }
  }, [isSuccess]);

  const handleAbort = useCallback(() => {
    setIsSAPHouse(!!house.isSAPHouse);
    setTestDailyDataEnabled(!!house.testDailyDataEnabled);
  }, [house.isSAPHouse, house.testDailyDataEnabled]);

  const handleAccept = useCallback(() => {
    patchHouse({
      houseId: house._id,
      patch: { isSAPHouse, testDailyDataEnabled },
    });
  }, [house._id, isSAPHouse, testDailyDataEnabled, patchHouse]);

  const checkboxId = `house-sap-toggle-${house._id}`;

  return (
    <EditableCard
      className={styles.sapSettingsCard}
      title={t('SAP-Konfiguration')}
      isEditing={isEditing}
      isLoading={isLoading}
      onEditingChange={setIsEditing}
      onAbort={handleAbort}
      onAccept={handleAccept}
      autoHeight
    >
      <div className={styles.row}>
        <div>
          <div className={styles.label}>{t('SAP-Haus')}</div>
          <div className={styles.description}>
            {t('Wenn aktiv, verwendet die Fahrer-App Frachtaufträge statt Touren.')}
          </div>
        </div>
        <label htmlFor={checkboxId} className={styles.toggle}>
          <input
            id={checkboxId}
            type="checkbox"
            checked={isSAPHouse}
            disabled={!isEditing || isLoading}
            onChange={(event) => setIsSAPHouse(event.target.checked)}
          />
          <span>{isSAPHouse ? t('Ja') : t('Nein')}</span>
        </label>
      </div>
      {['dev', 'test', 'local-dev'].includes(process.env.REACT_APP_DEPLOY_ENV) && (
        <div className={styles.row}>
          <div>
            <div className={styles.label}>{t('Automatische Test-Tagesdaten')}</div>
            <div className={styles.description}>
              {t('Erstellt täglich eine markierte Testtour bzw. einen Test-Frachtauftrag mit vorhandenen Teststopps.')}
            </div>
          </div>
          <label htmlFor={`house-test-data-toggle-${house._id}`} className={styles.toggle}>
            <input
              id={`house-test-data-toggle-${house._id}`}
              type="checkbox"
              checked={testDailyDataEnabled}
              disabled={!isEditing || isLoading}
              onChange={(event) => setTestDailyDataEnabled(event.target.checked)}
            />
            <span>{testDailyDataEnabled ? t('Ja') : t('Nein')}</span>
          </label>
        </div>
      )}
    </EditableCard>
  );
};

HouseSapSettingsCard.propTypes = {
  house: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    isSAPHouse: PropTypes.bool,
    testDailyDataEnabled: PropTypes.bool,
  }).isRequired,
};

export default HouseSapSettingsCard;
